/*
 * <product-loop-video>
 *
 * Auto-playing, muted, looping product video rendered as a PDP gallery slide
 * (see snippets/product-loop-video.liquid). Plays when the slide scrolls into
 * view and pauses when it leaves, with a bottom-right play/pause button whose
 * SVG ring tracks playback position.
 *
 * Swap-safe: all wiring lives in connectedCallback / disconnectedCallback so the
 * element re-initialises cleanly if product-info.js moves it during an AJAX
 * variant swap (no leaked observers/listeners, unlike the buy-buttons IIFE).
 */
if (!customElements.get('product-loop-video')) {
  customElements.define(
    'product-loop-video',
    class ProductLoopVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('.product-loop-video__video');
        this.toggle = this.querySelector('.product-loop-video__toggle');
        this.ring = this.querySelector('.product-loop-video__ring-progress');
        if (!this.video || !this.toggle) return;

        this.video.muted = true; // belt-and-suspenders: iOS only autoplays muted videos
        this.userPaused = false;
        this.inView = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.labelPlay = this.toggle.dataset.labelPlay || 'Play video';
        this.labelPause = this.toggle.dataset.labelPause || 'Pause video';

        this.onToggleClick = this.onToggleClick.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.tick = this.tick.bind(this);

        this.toggle.addEventListener('click', this.onToggleClick);
        this.video.addEventListener('play', this.onPlay);
        this.video.addEventListener('pause', this.onPause);

        // Play only while the slide is the dominant one on screen; pause otherwise.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              this.syncNavVisibility();
              if (this.inView) this.maybePlay();
              else this.video.pause();
            });
          },
          { threshold: [0, 0.5, 1] }
        );
        this.observer.observe(this);

        // Mobile gallery overlay (slide counter + a square "jump to video"
        // button). The overlay markup lives in product-media-gallery.liquid as a
        // sibling of the slide <ul>; we drive it from here so every listener is
        // set up/torn down with this element and stays swap-safe.
        this.galleryViewer = this.closest('slider-component');
        this.nav = this.galleryViewer && this.galleryViewer.querySelector('[data-loop-video-nav]');
        if (this.nav) {
          this.navToggle = this.nav.querySelector('[data-loop-video-jump]');
          this.navCurrent = this.nav.querySelector('.product-loop-video-nav__current');
          this.navTotal = this.nav.querySelector('.product-loop-video-nav__total');
          this.onNavToggleClick = this.onNavToggleClick.bind(this);
          this.onSlideChanged = this.onSlideChanged.bind(this);
          if (this.navToggle) this.navToggle.addEventListener('click', this.onNavToggleClick);
          this.galleryViewer.addEventListener('slideChanged', this.onSlideChanged);
          // Total = every gallery slide, including this injected video slide.
          if (this.navTotal) {
            const slideCount = this.galleryViewer.querySelectorAll('[id^="Slide-"]').length;
            if (slideCount) this.navTotal.textContent = slideCount;
          }
          this.syncNavVisibility();
        }

        // Reflect the element's real state (starts paused; play fires on view).
        this.setPlayingState(!this.video.paused);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
        if (this.navToggle) this.navToggle.removeEventListener('click', this.onNavToggleClick);
        if (this.galleryViewer) this.galleryViewer.removeEventListener('slideChanged', this.onSlideChanged);
        if (this.video) {
          this.video.removeEventListener('play', this.onPlay);
          this.video.removeEventListener('pause', this.onPause);
        }
        this.stopTicking();
      }

      maybePlay() {
        if (this.userPaused || this.reducedMotion) return;
        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // Swallow autoplay rejections (rare for muted inline video).
          playPromise.catch(() => {});
        }
      }

      onToggleClick() {
        if (this.video.paused) {
          // Explicit user intent overrides both manual-pause and reduced-motion.
          this.userPaused = false;
          this.reducedMotion = false;
          this.maybePlay();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      // Overlay "jump to video" button: scroll the gallery to this video slide,
      // then play it. Mirrors the in-slide toggle's "explicit user intent
      // overrides manual-pause and reduced-motion".
      onNavToggleClick() {
        const slide = this.closest('.product__media-item');
        const list = slide && slide.parentElement;
        if (slide && list) list.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        this.userPaused = false;
        this.reducedMotion = false;
        this.maybePlay();
      }

      // Keep the overlay counter in sync with the gallery's current slide.
      onSlideChanged(event) {
        if (!this.navCurrent) return;
        const page =
          (event.detail && event.detail.currentPage) ||
          (this.galleryViewer && this.galleryViewer.currentPage) ||
          1;
        this.navCurrent.textContent = page;
      }

      // While the video slide is on screen the in-slide toggle (with its
      // progress ring) is the control, so hide the overlay's jump button.
      // visibility (not display) keeps its box so the counter doesn't shift.
      syncNavVisibility() {
        if (this.nav) this.nav.classList.toggle('is-on-video', this.inView);
      }

      onPlay() {
        this.setPlayingState(true);
        this.startTicking();
      }

      onPause() {
        this.setPlayingState(false);
        this.stopTicking();
        // Resume after an *external* pause (e.g. window.pauseAllMedia() fired by
        // gallery navigation or the cart drawer) while the slide is still on
        // screen and the user has not paused it themselves.
        if (!this.userPaused && !this.reducedMotion && this.inView && !this.resuming) {
          this.resuming = true;
          requestAnimationFrame(() => {
            this.resuming = false;
            if (!this.userPaused && this.inView && this.video.paused) this.maybePlay();
          });
        }
      }

      setPlayingState(playing) {
        this.classList.toggle('is-paused', !playing);
        this.toggle.setAttribute('aria-pressed', String(playing));
        this.toggle.setAttribute('aria-label', playing ? this.labelPause : this.labelPlay);
      }

      startTicking() {
        this.stopTicking();
        this.raf = requestAnimationFrame(this.tick);
      }

      stopTicking() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      tick() {
        const duration = this.video.duration;
        if (this.ring && duration && isFinite(duration)) {
          const pct = Math.max(0, Math.min(100, (this.video.currentTime / duration) * 100));
          // pathLength="100" normalises the path (circle or rounded rect), so offset = remaining percent.
          this.ring.style.strokeDashoffset = (100 - pct).toFixed(2);
        }
        this.raf = requestAnimationFrame(this.tick);
      }
    }
  );
}
